import React, { useState, useMemo } from 'react';
import DriverProfileModal from './DriverProfileModal';

const vehicles = [
  {
    id: 'bike',
    label: 'Bike',
    emoji: '🚲',
    description: 'Quick & eco',
    basePrice: 50,
    pricePerKm: 15,
    eta: '3-5 min',
    capacity: '1 person',
    available: true,
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
        <circle cx="12" cy="34" r="8" stroke="currentColor" strokeWidth="2.5" />
        <circle cx="36" cy="34" r="8" stroke="currentColor" strokeWidth="2.5" />
        <path d="M12 34L20 18h8l8 16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20 18l4-6h6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="30" cy="12" r="2" fill="currentColor" />
      </svg>
    )
  },
  {
    id: 'boda',
    label: 'Boda Boda',
    emoji: '🏍️',
    description: 'Fast & affordable',
    basePrice: 80,
    pricePerKm: 20,
    eta: '2-4 min',
    capacity: '1 person',
    available: true,
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
        <circle cx="10" cy="34" r="7" stroke="currentColor" strokeWidth="2.5" />
        <circle cx="38" cy="34" r="7" stroke="currentColor" strokeWidth="2.5" />
        <path d="M10 34h28" stroke="currentColor" strokeWidth="2.5" />
        <path d="M17 34L22 20h8l6 14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 20l2-5h5l3 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <path d="M28 15h6l2 3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="24" cy="14" r="3" stroke="currentColor" strokeWidth="2" />
      </svg>
    )
  },
  {
    id: 'tuktuk',
    label: 'Tuk Tuk',
    emoji: '🛺',
    description: 'Comfy & local',
    basePrice: 120,
    pricePerKm: 25,
    eta: '5-8 min',
    capacity: '3 people',
    available: false,
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
        <rect x="8" y="16" width="28" height="18" rx="4" stroke="currentColor" strokeWidth="2.5" />
        <path d="M8 22h28" stroke="currentColor" strokeWidth="2" />
        <circle cx="14" cy="36" r="5" stroke="currentColor" strokeWidth="2.5" />
        <circle cx="30" cy="36" r="5" stroke="currentColor" strokeWidth="2.5" />
        <path d="M36 20l4-4h2v8h-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 22v12M22 22v12M30 22v12" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2" />
      </svg>
    )
  },
  {
    id: 'car',
    label: 'Car',
    emoji: '🚗',
    description: 'Premium comfort',
    basePrice: 200,
    pricePerKm: 35,
    eta: '6-10 min',
    capacity: '4 people',
    available: true,
    icon: (
      <svg viewBox="0 0 48 48" fill="none" className="w-10 h-10">
        <path d="M6 28l4-10h28l4 10v6H6v-6z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
        <path d="M10 18l3-6h22l3 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="13" cy="34" r="4" stroke="currentColor" strokeWidth="2.5" />
        <circle cx="35" cy="34" r="4" stroke="currentColor" strokeWidth="2.5" />
        <path d="M17 34h12" stroke="currentColor" strokeWidth="2" />
        <rect x="14" y="20" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
        <rect x="26" y="20" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    )
  }
];

const VEHICLE_TYPES = ['All', 'Bike', 'Boda Boda', 'Tuk Tuk', 'Car'];
const SORT_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'price_asc', label: 'Price: Low → High' },
  { value: 'price_desc', label: 'Price: High → Low' },
  { value: 'rating_desc', label: 'Rating: Best First' },
  { value: 'availability', label: 'Available First' },
];
const RATING_THRESHOLDS = [
  { value: 0, label: 'Any' },
  { value: 3, label: '3+' },
  { value: 4, label: '4+' },
  { value: 4.5, label: '4.5+' },
];

const StarBadge = ({ rating, isSelected }) => (
  <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-bold ${
    isSelected ? 'bg-[#ffd700]/20 text-[#ffd700]' : 'bg-[#ffd700]/10 text-[#0a1628]'
  }`}>
    <svg viewBox="0 0 12 12" fill="currentColor" className="w-2.5 h-2.5 flex-shrink-0">
      <path d="M6 1l1.4 2.8L10.5 4.3l-2.25 2.2.53 3.1L6 8.1 3.22 9.6l.53-3.1L1.5 4.3l3.1-.5z" />
    </svg>
    <span>{rating}</span>
  </div>
);

const VehiclePicker = ({ selectedVehicle, onVehicleChange, distance, driverRatings }) => {
  const [profileModal, setProfileModal] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Filter state
  const [ratingThreshold, setRatingThreshold] = useState(0);
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState('All');
  const [availabilityFilter, setAvailabilityFilter] = useState('all'); // 'all' | 'available' | 'unavailable'
  const [sortBy, setSortBy] = useState('default');

  const calculatePrice = (vehicle) => {
    const dist = distance || 3;
    return Math.round(vehicle?.basePrice + vehicle?.pricePerKm * dist);
  };

  const handleInfoClick = (e, vehicle) => {
    e?.stopPropagation();
    setProfileModal({ vehicleType: vehicle?.id, vehicleLabel: vehicle?.label });
  };

  const resetFilters = () => {
    setRatingThreshold(0);
    setPriceRange([0, 1000]);
    setVehicleTypeFilter('All');
    setAvailabilityFilter('all');
    setSortBy('default');
  };

  const hasActiveFilters = ratingThreshold > 0 || priceRange?.[0] > 0 || priceRange?.[1] < 1000 || vehicleTypeFilter !== 'All' || availabilityFilter !== 'all' || sortBy !== 'default';

  const filteredAndSortedVehicles = useMemo(() => {
    let result = vehicles?.map(v => ({ ...v, price: calculatePrice(v) }));

    // Filter: vehicle type
    if (vehicleTypeFilter !== 'All') {
      result = result?.filter(v => v?.label === vehicleTypeFilter);
    }

    // Filter: availability
    if (availabilityFilter === 'available') {
      result = result?.filter(v => v?.available);
    } else if (availabilityFilter === 'unavailable') {
      result = result?.filter(v => !v?.available);
    }

    // Filter: price range
    result = result?.filter(v => v?.price >= priceRange?.[0] && v?.price <= priceRange?.[1]);

    // Filter: rating threshold
    if (ratingThreshold > 0) {
      result = result?.filter(v => {
        const rd = driverRatings?.[v?.id];
        const avg = rd?.avgRating ? parseFloat(rd?.avgRating) : 0;
        return avg >= ratingThreshold;
      });
    }

    // Sort
    if (sortBy === 'price_asc') {
      result?.sort((a, b) => a?.price - b?.price);
    } else if (sortBy === 'price_desc') {
      result?.sort((a, b) => b?.price - a?.price);
    } else if (sortBy === 'rating_desc') {
      result?.sort((a, b) => {
        const ra = parseFloat(driverRatings?.[a?.id]?.avgRating || 0);
        const rb = parseFloat(driverRatings?.[b?.id]?.avgRating || 0);
        return rb - ra;
      });
    } else if (sortBy === 'availability') {
      result?.sort((a, b) => (b?.available ? 1 : 0) - (a?.available ? 1 : 0));
    }

    return result;
  }, [vehicleTypeFilter, availabilityFilter, priceRange, ratingThreshold, sortBy, distance, driverRatings]);

  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#0a1628]/60 uppercase tracking-wider">Choose Vehicle</h3>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="text-xs text-[#ffd700] font-medium hover:text-[#ffd700]/70 transition-colors"
              >
                Reset
              </button>
            )}
            <button
              onClick={() => setShowFilters(f => !f)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200 ${
                showFilters || hasActiveFilters
                  ? 'bg-[#0a1628] text-[#ffd700] shadow-md'
                  : 'bg-[#0a1628]/8 text-[#0a1628]/60 hover:bg-[#0a1628]/12'
              }`}
            >
              <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-3 h-3">
                <path d="M1 3h12M3 7h8M5 11h4" strokeLinecap="round" />
              </svg>
              Filters
              {hasActiveFilters && (
                <span className="w-4 h-4 rounded-full bg-[#ffd700] text-[#0a1628] text-[9px] font-bold flex items-center justify-center">
                  {[ratingThreshold > 0, priceRange?.[0] > 0 || priceRange?.[1] < 1000, vehicleTypeFilter !== 'All', availabilityFilter !== 'all', sortBy !== 'default']?.filter(Boolean)?.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-[#0a1628]/4 rounded-2xl p-3.5 space-y-3.5 border border-[#0a1628]/8">

            {/* Sort */}
            <div>
              <p className="text-[10px] font-bold text-[#0a1628]/50 uppercase tracking-wider mb-1.5">Sort By</p>
              <div className="flex flex-wrap gap-1.5">
                {SORT_OPTIONS?.map(opt => (
                  <button
                    key={opt?.value}
                    onClick={() => setSortBy(opt?.value)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all duration-150 ${
                      sortBy === opt?.value
                        ? 'bg-[#0a1628] text-[#ffd700]'
                        : 'bg-white text-[#0a1628]/60 border border-[#0a1628]/10 hover:border-[#0a1628]/25'
                    }`}
                  >
                    {opt?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Vehicle Type */}
            <div>
              <p className="text-[10px] font-bold text-[#0a1628]/50 uppercase tracking-wider mb-1.5">Vehicle Type</p>
              <div className="flex flex-wrap gap-1.5">
                {VEHICLE_TYPES?.map(type => (
                  <button
                    key={type}
                    onClick={() => setVehicleTypeFilter(type)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all duration-150 ${
                      vehicleTypeFilter === type
                        ? 'bg-[#0a1628] text-[#ffd700]'
                        : 'bg-white text-[#0a1628]/60 border border-[#0a1628]/10 hover:border-[#0a1628]/25'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Rating Threshold */}
            <div>
              <p className="text-[10px] font-bold text-[#0a1628]/50 uppercase tracking-wider mb-1.5">Min Rating</p>
              <div className="flex gap-1.5">
                {RATING_THRESHOLDS?.map(rt => (
                  <button
                    key={rt?.value}
                    onClick={() => setRatingThreshold(rt?.value)}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all duration-150 ${
                      ratingThreshold === rt?.value
                        ? 'bg-[#0a1628] text-[#ffd700]'
                        : 'bg-white text-[#0a1628]/60 border border-[#0a1628]/10 hover:border-[#0a1628]/25'
                    }`}
                  >
                    {rt?.value > 0 && (
                      <svg viewBox="0 0 10 10" fill="currentColor" className="w-2.5 h-2.5">
                        <path d="M5 1l1.2 2.4L9 3.8l-2 1.9.5 2.7L5 7.1 2.5 8.4l.5-2.7L1 3.8l2.8-.4z" />
                      </svg>
                    )}
                    {rt?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[10px] font-bold text-[#0a1628]/50 uppercase tracking-wider">Price Range</p>
                <span className="text-[10px] font-semibold text-[#0a1628]/60">
                  KSh {priceRange?.[0]} – {priceRange?.[1] >= 1000 ? '1000+' : priceRange?.[1]}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#0a1628]/40 w-6">Min</span>
                  <input
                    type="range"
                    min={0}
                    max={900}
                    step={50}
                    value={priceRange?.[0]}
                    onChange={e => {
                      const val = parseInt(e?.target?.value);
                      if (val < priceRange?.[1]) setPriceRange([val, priceRange?.[1]]);
                    }}
                    className="flex-1 h-1.5 rounded-full accent-[#0a1628] cursor-pointer"
                  />
                  <span className="text-[10px] text-[#0a1628]/60 w-10 text-right font-medium">KSh {priceRange?.[0]}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#0a1628]/40 w-6">Max</span>
                  <input
                    type="range"
                    min={100}
                    max={1000}
                    step={50}
                    value={priceRange?.[1]}
                    onChange={e => {
                      const val = parseInt(e?.target?.value);
                      if (val > priceRange?.[0]) setPriceRange([priceRange?.[0], val]);
                    }}
                    className="flex-1 h-1.5 rounded-full accent-[#0a1628] cursor-pointer"
                  />
                  <span className="text-[10px] text-[#0a1628]/60 w-10 text-right font-medium">{priceRange?.[1] >= 1000 ? '1000+' : `KSh ${priceRange?.[1]}`}</span>
                </div>
              </div>
            </div>

            {/* Availability */}
            <div>
              <p className="text-[10px] font-bold text-[#0a1628]/50 uppercase tracking-wider mb-1.5">Availability</p>
              <div className="flex gap-1.5">
                {[
                  { value: 'all', label: 'All' },
                  { value: 'available', label: '✓ Available' },
                  { value: 'unavailable', label: '✗ Unavailable' },
                ]?.map(opt => (
                  <button
                    key={opt?.value}
                    onClick={() => setAvailabilityFilter(opt?.value)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all duration-150 ${
                      availabilityFilter === opt?.value
                        ? 'bg-[#0a1628] text-[#ffd700]'
                        : 'bg-white text-[#0a1628]/60 border border-[#0a1628]/10 hover:border-[#0a1628]/25'
                    }`}
                  >
                    {opt?.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Results count */}
        {hasActiveFilters && (
          <p className="text-xs text-[#0a1628]/40">
            Showing <span className="font-semibold text-[#0a1628]/70">{filteredAndSortedVehicles?.length}</span> of {vehicles?.length} vehicles
          </p>
        )}

        {/* Vehicle Grid */}
        {filteredAndSortedVehicles?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="text-3xl mb-2">🔍</div>
            <p className="text-sm font-semibold text-[#0a1628]/60">No vehicles match your filters</p>
            <button onClick={resetFilters} className="mt-2 text-xs text-[#ffd700] font-semibold hover:underline">
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2.5">
            {filteredAndSortedVehicles?.map((vehicle) => {
              const price = vehicle?.price;
              const isSelected = selectedVehicle === vehicle?.id;
              const ratingData = driverRatings?.[vehicle?.id];
              const hasRating = ratingData?.avgRating && parseFloat(ratingData?.avgRating) > 0;
              return (
                <button
                  key={vehicle?.id}
                  onClick={() => vehicle?.available && onVehicleChange(vehicle?.id)}
                  disabled={!vehicle?.available}
                  className={`relative flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all duration-300 text-left ${
                    !vehicle?.available
                      ? 'border-[#0a1628]/8 bg-[#0a1628]/4 opacity-60 cursor-not-allowed'
                      : isSelected
                        ? 'border-[#ffd700] bg-[#0a1628] shadow-lg scale-[1.02]'
                        : 'border-[#0a1628]/10 bg-white hover:border-[#ffd700]/50 hover:bg-[#ffd700]/5'
                  }`}
                >
                  {/* Unavailable badge */}
                  {!vehicle?.available && (
                    <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded-full bg-red-100 text-red-500 text-[9px] font-bold uppercase tracking-wide">
                      Unavailable
                    </div>
                  )}
                  {isSelected && vehicle?.available && (
                    <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#ffd700] flex items-center justify-center">
                      <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3">
                        <path d="M2 6l3 3 5-5" stroke="#0a1628" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  )}
                  {/* Info button (only when not selected and available) */}
                  {!isSelected && vehicle?.available && (
                    <button
                      onClick={(e) => handleInfoClick(e, vehicle)}
                      className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#0a1628]/10 flex items-center justify-center hover:bg-[#0a1628]/20 transition-colors"
                      title={`View ${vehicle?.label} driver profile`}
                    >
                      <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-3 h-3 text-[#0a1628]/60">
                        <circle cx="6" cy="6" r="5" />
                        <path d="M6 5.5v3M6 3.5v.5" strokeLinecap="round" />
                      </svg>
                    </button>
                  )}
                  <div className={`transition-colors ${isSelected ? 'text-[#ffd700]' : 'text-[#0a1628]/70'}`}>
                    {vehicle?.icon}
                  </div>
                  <div className="w-full">
                    <div className={`font-bold text-sm ${isSelected ? 'text-white' : 'text-[#0a1628]'}`}>
                      {vehicle?.label}
                    </div>
                    <div className={`text-xs mt-0.5 ${isSelected ? 'text-[#ffd700]/80' : 'text-[#0a1628]/50'}`}>
                      {vehicle?.description}
                    </div>
                  </div>
                  {/* Driver Rating Row */}
                  {hasRating ? (
                    <div className="w-full flex items-center gap-1.5">
                      <StarBadge rating={ratingData?.avgRating} isSelected={isSelected} />
                      <span className={`text-xs ${isSelected ? 'text-white/60' : 'text-[#0a1628]/40'}`}>
                        ({ratingData?.reviewCount} {ratingData?.reviewCount === 1 ? 'review' : 'reviews'})
                      </span>
                    </div>
                  ) : (
                    <div className="w-full flex items-center gap-1">
                      <svg viewBox="0 0 12 12" fill="none" className={`w-2.5 h-2.5 ${isSelected ? 'text-white/30' : 'text-[#0a1628]/25'}`} stroke="currentColor" strokeWidth="1.5">
                        <path d="M6 1l1.4 2.8L10.5 4.3l-2.25 2.2.53 3.1L6 8.1 3.22 9.6l.53-3.1L1.5 4.3l3.1-.5z" />
                      </svg>
                      <span className={`text-xs ${isSelected ? 'text-white/40' : 'text-[#0a1628]/30'}`}>New</span>
                    </div>
                  )}
                  <div className="w-full flex items-center justify-between">
                    <span className={`text-xs font-medium ${isSelected ? 'text-[#ffd700]/70' : 'text-[#0a1628]/40'}`}>
                      {vehicle?.eta}
                    </span>
                    <span className={`text-sm font-bold ${isSelected ? 'text-[#ffd700]' : 'text-[#0a1628]'}`}>
                      KSh {price}
                    </span>
                  </div>
                  {/* "View driver" tap hint on selected card */}
                  {isSelected && vehicle?.available && (
                    <button
                      onClick={(e) => handleInfoClick(e, vehicle)}
                      className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-xl bg-white/10 hover:bg-white/15 transition-colors"
                    >
                      <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-3 h-3 text-[#ffd700]/80">
                        <circle cx="7" cy="5" r="2.5" />
                        <path d="M2 12c0-2.8 2.2-5 5-5s5 2.2 5 5" strokeLinecap="round" />
                      </svg>
                      <span className="text-xs text-[#ffd700]/80 font-medium">View driver profile</span>
                    </button>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {/* Driver Profile Modal */}
      {profileModal && (
        <DriverProfileModal
          vehicleType={profileModal?.vehicleType}
          vehicleLabel={profileModal?.vehicleLabel}
          onClose={() => setProfileModal(null)}
        />
      )}
    </>
  );
};

export { vehicles };
export default VehiclePicker;
